import { on, reducer } from "ts-action";
import { withLoadingReducer } from "../../../Components/Pickup/redux-store/reducers/withLoadingState";
import { getVouchersAction } from "../../../constants";
import { IPromocode } from "../../../types";
import * as actions from "../../actions";
import { ReferralGiftInfo } from "../menu-reducers/referralMenuReducer";

export interface VouchersState {
  vouchers: IPromocode[];
  promoList: IPromocode[];
  shownPromoList: IPromocode[];
  senderPromoList: IPromocode[];
  existingGiftsMap: Map<string, ReferralGiftInfo>;
  existingGiftsMapBackup?: Map<string, ReferralGiftInfo>;
  loadingStatus?: string;
}

const initialState: VouchersState = {
  vouchers: [],
  promoList: [],
  shownPromoList: [],
  senderPromoList: [],
  existingGiftsMap: new Map(),
  existingGiftsMapBackup: new Map(),
};

const filterPromoListByExistingGifts = (
  promoList: IPromocode[],
  existingGiftsMap: Map<string, ReferralGiftInfo>
): IPromocode[] => {
  const existingIds = new Set(existingGiftsMap.keys());
  return promoList.filter((promo) => !existingIds.has(promo.id));
};

export default withLoadingReducer<VouchersState>(
  reducer<VouchersState>(
    [
      on(actions.getVouchers, (state) => ({
        ...state,
        promoList: [],
        shownPromoList: [],
        senderPromoList: [],
      })),

      on(actions.getVouchersSuccess, (state, { payload }) => {
        if (payload.is_voucher) {
          const filteredPromoList = filterPromoListByExistingGifts(
            payload.data,
            state.existingGiftsMap
          );
          return {
            ...state,
            promoList: payload.data,
            shownPromoList: filteredPromoList,
            senderPromoList: payload.data,
          };
        }
        return {
          ...state,
          vouchers: payload.data,
        };
      }),

      on(actions.getVouchersFailure, () => initialState),

      on(actions.getReferralGiftsSuccess, (state, { payload }) => {
        const existingGiftsMap = new Map<string, ReferralGiftInfo>();
        payload.forEach((gift, index) => {
          if (gift.recipient_referral_gift_id) {
            existingGiftsMap.set(gift.recipient_referral_gift_id, {
              id: gift.recipient_referral_gift_id,
              columnType: "recipient",
              index,
              referral_id: gift.referral_id,
            });
          }
        });
        return {
          ...state,
          existingGiftsMap,
          existingGiftsMapBackup: existingGiftsMap,
          shownPromoList: filterPromoListByExistingGifts(
            state.promoList,
            existingGiftsMap
          ),
          senderPromoList: state.promoList,
        };
      }),

      on(actions.modifyReferralGift, (state, { payload }) => {
        const { data, rowIndex, prev_recipient_referral_gift_id } = payload;
        let newExistingGiftsMap = new Map(state.existingGiftsMap);

        for (const [key, info] of Array.from(state.existingGiftsMap.entries())) {
          if (info.index === rowIndex) {
            newExistingGiftsMap.delete(key);
          }
        }

        if (prev_recipient_referral_gift_id) {
          newExistingGiftsMap.delete(prev_recipient_referral_gift_id);
        }

        if (payload.columnType === "recipient" && data?.recipient_referral_gift_id) {
          newExistingGiftsMap.set(data.recipient_referral_gift_id, {
            id: data.recipient_referral_gift_id,
            columnType: "recipient",
            index: rowIndex,
            referral_id: data.referral_id,
          });
        }

        return {
          ...state,
          existingGiftsMap: newExistingGiftsMap,
          shownPromoList: filterPromoListByExistingGifts(
            state.promoList,
            newExistingGiftsMap
          ),
          senderPromoList: state.promoList,
        };
      }),

      on(actions.modifyReferralGiftSuccess, (state, { payload }) => {
        const newExistingGiftsMap = new Map<string, ReferralGiftInfo>();

        payload.forEach((gift, index) => {
          if (gift.recipient_referral_gift_id) {
            newExistingGiftsMap.set(gift.recipient_referral_gift_id, {
              id: gift.recipient_referral_gift_id,
              columnType: "recipient",
              index,
              referral_id: gift.referral_id,
            });
          }
        });

        return {
          ...state,
          existingGiftsMap: newExistingGiftsMap,
          shownPromoList: filterPromoListByExistingGifts(
            state.promoList,
            newExistingGiftsMap
          ),
          senderPromoList: state.promoList,
        };
      }),

      on(actions.deleteReferralGiftSuccess, (state, { payload }) => {
        const newExistingGiftsMap = new Map(state.existingGiftsMap);

        if (payload.sender_referral_gift_id) {
          newExistingGiftsMap.delete(payload.sender_referral_gift_id);
        }
        if (payload.recipient_referral_gift_id) {
          newExistingGiftsMap.delete(payload.recipient_referral_gift_id);
        }

        return {
          ...state,
          existingGiftsMap: newExistingGiftsMap,
          existingGiftsMapBackup: newExistingGiftsMap,
          shownPromoList: filterPromoListByExistingGifts(
            state.promoList,
            newExistingGiftsMap
          ),
          senderPromoList: state.promoList,
        };
      }),

      on(actions.modifyReferralGiftFailure, (state, { payload }) => {
        const filteredPromoList = filterPromoListByExistingGifts(
          state.promoList,
          state.existingGiftsMapBackup || state.existingGiftsMap
        );

        return {
          ...state,
          existingGiftsMap: state.existingGiftsMapBackup || new Map(),
          shownPromoList: filteredPromoList,
          senderPromoList: state.promoList,
        };
      }),

      on(actions.removeEmptyReferralGift, (state, { payload }) => {
        const newExistingGiftsMap = new Map(state.existingGiftsMap);
        const gift = payload.gift;

        if (gift.recipient_referral_gift_id) {
          newExistingGiftsMap.delete(gift.recipient_referral_gift_id);
        }

        const updatedShownPromoList = filterPromoListByExistingGifts(
          state.promoList,
          newExistingGiftsMap
        );

        return {
          ...state,
          existingGiftsMap: newExistingGiftsMap,
          existingGiftsMapBackup: newExistingGiftsMap,
          shownPromoList: updatedShownPromoList,
          senderPromoList: state.promoList,
        };
      }),
    ],
    initialState
  ),
  getVouchersAction
);
